import React, { useEffect, useMemo, useRef, useState } from 'react';
import { shuffle } from 'lodash';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const CONFIRM_MS = 280;
// If the store's double-submit guard swallowed the answer, the panel is still here:
// unlock it so the respondent is never stuck.
const UNLOCK_MS = 2500;

// Same option sources as v1's Buttons (action.options) and Multi (action.multi).
const buildOptions = (action, multi) => {
  const source = (multi ? action.multi : action.options) || action;
  const list = Object.keys(source)
    .filter((key) => key !== 'next_q')
    .map((key) => ({ text: source[key].text, value: source[key].value, next_q: source[key].next_q }));
  return action.shuffle === 1 ? shuffle(list) : list;
};

const Check = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-6.5" />
  </svg>
);

function V2Choice({ current, sendResponse, multi }) {
  const action = current.action || {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(() => buildOptions(action, multi), [current.id]);
  const max = multi ? action.max || options.length : 1;
  const [picked, setPicked] = useState([]);
  const sent = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => (mounted.current = false), []);

  const send = (data, text, keepPicks) => {
    sendResponse(data, text, current);
    setTimeout(() => {
      if (!mounted.current) return;
      sent.current = false;
      if (!keepPicks) setPicked([]);
    }, UNLOCK_MS);
  };

  const submitMulti = (chosen) => {
    if (sent.current || chosen.length === 0) return;
    sent.current = true;
    // List order, not click order: v1 saves multi answers in the order the options are shown.
    const items = [...chosen].sort((a, b) => a - b).map((i) => options[i]);
    const data = { value: items.map((o) => o.value).join() };
    if (action.next_q) data.next_q = action.next_q;
    send(data, items.map((o) => o.text).join(', '), true);
  };

  const choose = (i) => {
    if (sent.current || !options[i]) return;
    if (!multi) {
      // Let the pick register visually before the question moves on.
      sent.current = true;
      setPicked([i]);
      setTimeout(() => send(options[i], options[i].text, false), CONFIRM_MS);
      return;
    }
    setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : p.length >= max ? p : [...p, i]));
  };

  // Keyboard: A–Z or 1–9 picks, Enter sends a multiple choice.
  const latest = useRef({ choose, submitMulti, picked });
  latest.current = { choose, submitMulti, picked };
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || /input|textarea|select/i.test(e.target.tagName)) return;
      const key = e.key.toUpperCase();
      if (key === 'ENTER' && multi) return latest.current.submitMulti(latest.current.picked);
      const index = /^[1-9]$/.test(key) ? Number(key) - 1 : LETTERS.indexOf(key);
      if (index >= 0 && key.length === 1) latest.current.choose(index);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multi]);

  const full = multi && picked.length >= max;
  return (
    <div className={`v2-choice${multi ? ' v2-choice--multi' : ''}${options.length > 6 ? ' is-many' : ''}`}>
      <div className="v2-choice__list" role={multi ? 'group' : 'radiogroup'}>
        {options.map((o, i) => {
          const on = picked.includes(i);
          return (
            <button
              key={`${o.value}_${i}`}
              type="button"
              className={`v2-option${on ? ' is-on' : ''}${full && !on ? ' is-off' : ''}`}
              style={{ animationDelay: `${60 + i * 35}ms` }}
              aria-pressed={on}
              onClick={() => choose(i)}
            >
              <span className="v2-option__key">{on ? <Check /> : LETTERS[i]}</span>
              <span className="v2-option__text">{o.text}</span>
            </button>
          );
        })}
      </div>
      {multi && (
        <div className="v2-choice__bar">
          <span className="v2-choice__count">
            {picked.length === 0
              ? max < options.length
                ? `Välj upp till ${max}`
                : 'Välj ett eller flera'
              : `${picked.length} av ${max < options.length ? max : options.length} valda`}
          </span>
          <button
            type="button"
            className="v2-next"
            disabled={picked.length === 0}
            onClick={() => submitMulti(picked)}
          >
            Fortsätt
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M4 10h11M11 5l5 5-5 5" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default V2Choice;
